import { AddIcon, DotsIcon, GearIcon } from '@approvalmax/ui';
import { Button, Dropdown, Flex, PageWrapper, Popup } from '@approvalmax/ui/src/components';
import { errorHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { memo, useMemo } from 'react';
import { useToggle } from 'react-use';
import { HelpStripe } from 'shared/components';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { qBooksBudgetCheckingHelpStripeId, xeroBudgetCheckingHelpStripeId } from '../../constants';
import { useIntegrationTypeDecision } from '../../hooks/useIntegrationTypeDecision';
import QBooksDisableSyncMenuItem from '../QBooksDisableSyncMenuItem/QBooksDisableSyncMenuItem';
import { QBooksEnableSyncMenuItem } from '../QBooksEnableSyncMenuItem/QBooksEnableSyncMenuItem';
import { QBooksSyncBudgetButton } from '../QBooksSyncBudgetButton/QBooksSyncBudgetButton';
import SettingsPopupContent from '../SettingsPopupContent/SettingsPopupContent';
import XeroDisableSyncMenuItem from '../XeroDisableSyncMenuItem/XeroDisableSyncMenuItem';
import XeroSyncBudgetButton from '../XeroSyncBudgetButton/XeroSyncBudgetButton';
import { messages } from './Toolbar.messages';
import { ToolbarProps } from './Toolbar.types';

export const Toolbar = memo<ToolbarProps>((props) => {
    const {
        isDisabled,
        canUploadBudget,
        showSettings = true,
        isBudgetSyncEnabled,
        isSyncInProgress,
        onUploadNewBudget,
    } = props;

    const [isSettingsOpen, toggleIsSettingsOpen] = useToggle(false);

    const company = useCompanyBasedOnUrl();

    const integrationTypeDecision = useIntegrationTypeDecision();

    const isDisconnected = !company.flags.hasActiveIntegration;
    const isReadOnly = !company.flags.isManager;

    const syncButton = useMemo(() => {
        switch (integrationTypeDecision) {
            case domain.IntegrationType.Xero:
                return <XeroSyncBudgetButton />;

            case domain.IntegrationType.QBooks:
                return (
                    <QBooksSyncBudgetButton
                        isBudgetSyncEnabled={isBudgetSyncEnabled}
                        isSyncInProgress={isSyncInProgress}
                    />
                );

            default:
                throw errorHelpers.notImplementedError();
        }
    }, [integrationTypeDecision, isBudgetSyncEnabled, isSyncInProgress]);

    const disableSyncMenuItem = useMemo(() => {
        if (!isBudgetSyncEnabled) {
            return null;
        }

        switch (integrationTypeDecision) {
            case domain.IntegrationType.Xero:
                return <XeroDisableSyncMenuItem />;

            case domain.IntegrationType.QBooks:
                return <QBooksDisableSyncMenuItem />;

            default:
                return null;
        }
    }, [integrationTypeDecision, isBudgetSyncEnabled]);

    const enableSyncMenuItem = useMemo(() => {
        if (isBudgetSyncEnabled) {
            return null;
        }

        switch (integrationTypeDecision) {
            case domain.IntegrationType.QBooks:
                return <QBooksEnableSyncMenuItem />;

            default:
                return null;
        }
    }, [integrationTypeDecision, isBudgetSyncEnabled]);

    const helpStripeId = useMemo(() => {
        switch (integrationTypeDecision) {
            case domain.IntegrationType.Xero:
                return xeroBudgetCheckingHelpStripeId;

            case domain.IntegrationType.QBooks:
                return qBooksBudgetCheckingHelpStripeId;

            default:
                return null;
        }
    }, [integrationTypeDecision]);

    const showToolbarButtons = !isReadOnly && !isDisabled;

    const showActionMenu = Boolean(disableSyncMenuItem || enableSyncMenuItem);

    if (isDisconnected) {
        return null;
    }

    return (
        <>
            <PageWrapper.Toolbar>
                {showToolbarButtons && (
                    <Flex justifyContent='space-between'>
                        <Flex spacing='16' grow={1} alignItems='center'>
                            {canUploadBudget && (
                                <Button
                                    startIcon={<AddIcon />}
                                    color='blue80'
                                    disabled={isDisabled || company.isReadonly}
                                    onClick={onUploadNewBudget}
                                    size='small'
                                >
                                    {messages.uploadNewBudget}
                                </Button>
                            )}

                            {syncButton}

                            {showSettings && (
                                <Button
                                    icon
                                    noPadding
                                    onClick={toggleIsSettingsOpen}
                                    disabled={isDisabled || company.isReadonly}
                                    title={messages.settings}
                                    variant='text'
                                    color='blue100'
                                >
                                    <GearIcon size={28} />
                                </Button>
                            )}

                            {showActionMenu && (
                                <Dropdown
                                    width='max-content'
                                    activator={
                                        <Button
                                            icon
                                            noPadding
                                            disabled={isDisabled}
                                            title={messages.showActionMenu}
                                            variant='text'
                                            color='blue100'
                                        >
                                            <DotsIcon size={28} />
                                        </Button>
                                    }
                                >
                                    {disableSyncMenuItem}

                                    {enableSyncMenuItem}
                                </Dropdown>
                            )}
                        </Flex>

                        {helpStripeId && (
                            <Flex spacing='16' alignItems='center'>
                                <HelpStripe.ToggleButton id={helpStripeId} />
                            </Flex>
                        )}
                    </Flex>
                )}

                {helpStripeId && <HelpStripe id={helpStripeId} />}
            </PageWrapper.Toolbar>

            {showSettings && (
                <Popup open={isSettingsOpen} onToggle={toggleIsSettingsOpen} size='large'>
                    <SettingsPopupContent toggleIsOpenPopup={toggleIsSettingsOpen} />
                </Popup>
            )}
        </>
    );
});

Toolbar.displayName = 'Toolbar';
